import { useDeleteHostAuthenticationTokenForHost } from "@/queries/runner-configuration-queries";
import { useIsUserAuthenticatedWithRunner, useParseContext } from "@/queries/runner-queries.ts";
import { useCallback, useEffect, useMemo, useState } from "react";

export type SCMAuthenticationStage = "required" | "pending" | "verifying" | "successful" | "error" | "out_of_sync";

export type UseSCMAuthenticationProps = {
    runnerId: string;
    repoURL: string;
    authenticationUrl?: string;
};

export const useSCMAuthentication = ({ repoURL, runnerId, authenticationUrl }: UseSCMAuthenticationProps) => {
    const parseContext = useParseContext();
    const deleteToken = useDeleteHostAuthenticationTokenForHost();

    const [stage, setStage] = useState<SCMAuthenticationStage>("required");
    const [verificationError, setError] = useState<Error | undefined>();

    // Detect if repoURL is host-only (e.g., "https://github.com") vs full repo URL
    const isHostOnlyUrl = useMemo(() => {
        try {
            const pathname = new URL(repoURL).pathname;
            return pathname === "/" || pathname === "";
        } catch {
            return false;
        }
    }, [repoURL]);

    const { data: authCheckResponse } = useIsUserAuthenticatedWithRunner(runnerId, repoURL, {
        refetchUntilAuthenticated: true,
        enabled: stage !== "successful",
    });

    const continueWithVerifying = useCallback(async () => {
        // Skip context parsing for host-only URLs, e.g. from OAuth flows on the Git Authentications page, these don't
        // have a specific repo url to verify against.
        if (isHostOnlyUrl) {
            setStage("successful");
            return;
        }

        try {
            await parseContext.mutateAsync({
                contextUrl: repoURL,
                runnerId: runnerId,
            });
            setStage("successful");
        } catch (error) {
            setError(error instanceof Error ? error : new Error("Failed to verify authentication"));
            setStage("error");
        }
    }, [runnerId, parseContext, repoURL, isHostOnlyUrl]);

    useEffect(() => {
        if (stage === "pending" && authCheckResponse?.type === "Authenticated") {
            setStage("verifying");
            continueWithVerifying().catch(console.error);
        } else if (stage === "required" && authCheckResponse?.type === "Authenticated") {
            setStage("out_of_sync");
        }
    }, [stage, authCheckResponse?.type, continueWithVerifying]);

    const handleConnectWithOAuth = useCallback(() => {
        if (stage === "required") {
            window.open(authenticationUrl, "_blank", "noopener");
            setStage("pending");
        }
    }, [authenticationUrl, stage]);

    const handleConnectWithPAT = useCallback(async () => {
        if (stage === "required") {
            await continueWithVerifying();
        }
    }, [continueWithVerifying, stage]);

    const handleRemoveConnection = useCallback(async () => {
        try {
            await deleteToken.mutateAsync({ scmHost: new URL(repoURL).host, runnerId });
        } catch (error) {
            console.error("Failed to remove connection", error);
        }
    }, [runnerId, deleteToken, repoURL]);

    const reset = useCallback(() => {
        setStage("required");
        setError(undefined);
    }, []);

    return {
        stage,
        reset,
        verificationError,
        handleConnectWithOAuth,
        handleConnectWithPAT,
        handleRemoveConnection,
    };
};
